import { type StoreAnalytics, type StorePrivacy } from 'ecosystem'
import {
  type ConsentState,
  type DataCollectionConfig,
  type Destination,
  type GoogleConsentSettings
} from './typings'

export const DEFAULT_GTM_SCRIPT_ORIGIN = 'https://www.googletagmanager.com'

interface ResolveParams {
  storeId: string
  isDebugMode?: boolean
  analytics?: StoreAnalytics
  privacy?: StorePrivacy
  /** Call-site override, still beaten by an explicit `privacy.defaultConsent`. */
  defaultConsentState?: Partial<Omit<ConsentState, 'client_timestamp'>>
}

/** Trailing slashes would produce `https://host//gtm.js`. */
const normaliseOrigin = (origin?: string): string =>
  origin?.trim().replace(/\/+$/, '') || DEFAULT_GTM_SCRIPT_ORIGIN

/**
 * Applies every default in one place, so the managers can treat the config as
 * fully populated.
 *
 * Defaults reproduce the previous behaviour for everything except
 * `loadStrategy`, which now defaults to `HTML` — the standard snippet rendered
 * into the page by `GtmBootstrap` instead of injected after hydration.
 * `AFTER_INTERACTIVE` and `ON_LOAD` keep the client-side injection.
 */
export function resolveDataCollectionConfig({
  storeId,
  isDebugMode,
  analytics,
  privacy,
  defaultConsentState
}: ResolveParams): DataCollectionConfig {
  const destinations: Destination[] =
    analytics?.destinations?.map((destination) => ({
      ...destination,
      type: destination.type.toLowerCase() as Destination['type']
    })) ?? []

  // A store that never set `mode` is CENTRAL exactly when it has somewhere to
  // route to — otherwise the router metadata is pure noise in the payload.
  const mode = analytics?.mode ?? (destinations.length ? 'CENTRAL' : 'DIRECT')

  // The merchant's CMP owns the Consent Mode defaults when it lives inside their
  // container: a second `default` racing their restored `update` can downgrade a
  // returning visitor who had already consented.
  const manageConsentDefaults =
    privacy?.manageConsentDefaults ?? privacy?.cmpType !== 'CONSENT_MODE'

  return {
    storeId,
    isDebugMode: isDebugMode ?? false,
    cmpConfig: privacy,
    gtmConfig: {
      centralContainerId: analytics?.gtmId ?? ''
    },
    consentSettings: {
      defaultConsentState: {
        analytics: false,
        marketing: false,
        preferences: false,
        ...defaultConsentState,
        ...privacy?.defaultConsent
      }
    },
    destinations: mode === 'DIRECT' ? [] : destinations,
    mode,
    loadStrategy: analytics?.loadStrategy ?? 'HTML',
    scriptOrigin: normaliseOrigin(analytics?.scriptOrigin),
    sendPageView: analytics?.sendPageView ?? true,
    manageConsentDefaults
  }
}

export const CENTRAL_DATA_LAYER_NAME = 'dataLayer'

/** Each destination container gets its own data layer, as GTM's `l` parameter. */
export const destinationDataLayerName = (destination: Pick<Destination, 'id'>): string =>
  `dataLayer_${destination.id.replace(/-/g, '_')}`

/**
 * Every data layer on the page. Consent defaults, consent updates and the
 * Consent Mode observer all have to cover all of them — a destination container
 * keeps its own consent state, derived from its own layer.
 */
export const dataLayerNames = (destinations: Destination[]): string[] => [
  CENTRAL_DATA_LAYER_NAME,
  ...destinations.filter((destination) => destination.type === 'gtm').map(destinationDataLayerName)
]

/** Our three categories expanded into the seven Google Consent Mode signals. */
export const toGoogleConsent = (
  state: Omit<ConsentState, 'client_timestamp'>
): GoogleConsentSettings => ({
  ad_storage: state.marketing ? 'granted' : 'denied',
  analytics_storage: state.analytics ? 'granted' : 'denied',
  functionality_storage: state.preferences ? 'granted' : 'denied',
  personalization_storage: state.marketing ? 'granted' : 'denied',
  ad_personalization: state.marketing ? 'granted' : 'denied',
  ad_user_data: state.marketing ? 'granted' : 'denied',
  security_storage: 'granted'
})

/**
 * Closing a script tag from inside its own body ends it early — the values below
 * come from store settings, so neutralise the sequence.
 */
export const inlineJson = (value: unknown): string =>
  JSON.stringify(value).replace(/<\//g, '<\\/').replace(/<!--/g, '<\\!--')

/**
 * The whole GTM bootstrap as one inline script: data layer, Consent Mode default
 * and the standard container snippet, per container.
 *
 * Keeping them in ONE synchronous block is the point. `gtm.js` is `async`, so it
 * cannot execute until this block finishes — which makes "consent default before
 * the container reads it" a guarantee of JS being single-threaded rather than a
 * race. Splitting the default out (into a React effect, or a second tag) is what
 * breaks Consent Mode: the container runs first, sees no default, decides consent
 * mode is not in use, and its tags set cookies without consent.
 */
export const buildGtmBootstrapScript = (config: DataCollectionConfig): string => {
  const consent = toGoogleConsent(config.consentSettings.defaultConsentState)

  const containers = [
    { id: config.gtmConfig.centralContainerId, layer: CENTRAL_DATA_LAYER_NAME },
    ...config.destinations
      .filter((destination) => destination.type === 'gtm')
      .map((destination) => ({
        id: destination.id,
        layer: destinationDataLayerName(destination)
      }))
  ]

  const consentBlock = config.manageConsentDefaults
    ? `  gtag('consent', 'default', Object.assign({}, ${inlineJson(consent)}, { wait_for_update: 500 }));
  gtag('set', 'ads_data_redaction', ${consent.ad_storage === 'denied'});
  gtag('set', 'url_passthrough', false);
  w[l].push({ event: 'consent_update', consent: ${inlineJson(consent)} });`
    : '  /* Consent Mode defaults are owned by the merchant CMP */'

  return containers
    .map(
      ({ id, layer }) => `(function(w,d,s,l,i,o){
  w[l] = w[l] || [];
  function gtag(){ w[l].push(arguments); }
${consentBlock}
  w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  var f = d.getElementsByTagName(s)[0], j = d.createElement(s);
  j.async = true;
  j.src = o + '/gtm.js?id=' + i + '&l=' + l;
  f.parentNode.insertBefore(j, f);
})(window,document,'script',${inlineJson(layer)},${inlineJson(id)},${inlineJson(config.scriptOrigin)});`
    )
    .join('\n')
}
